import { FC } from 'react';

import { Col, Flex, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResult } from '@/types/simulations';
import { formatNumberWithCommas } from '@/utils';

import StyledStorage from './styles';

interface StorageTableProps {
  simulationData: SimulationResult;
  showNumbers?: boolean;
}

const StorageTable: FC<StorageTableProps> = ({
  simulationData,
  showNumbers,
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const symbol = currencySymbol || '$';

  return (
    <StyledStorage>
      <Col span={10}>
        <Row>
          <Col span={17}>
            <Flex vertical align="center">
              <Flex align="center" className="column">
                <Typography.Text className="fs-14-bold">
                  {t('result.analysis.storageTable.averageMcp')}
                  {showNumbers && (
                    <span className="text-number-color"> (1)</span>
                  )}
                </Typography.Text>
                <Tooltip
                  title={t('result.analysis.storageTable.averageMcp')}
                  description={t(
                    'result.analysis.storageTable.averageMcpDescription'
                  )}
                >
                  <IconInfo fill="black" />
                </Tooltip>
              </Flex>
              <Flex gap={9} align="center" className="column">
                <Typography.Text className="fs-14-bold">
                  {t(
                    'result.analysis.storageTable.weightedAverageSalesPriceBeforeStorage'
                  )}
                  {showNumbers && (
                    <span className="text-number-color"> (2)</span>
                  )}
                </Typography.Text>
                <Tooltip
                  title={t(
                    'result.analysis.storageTable.weightedAverageSalesPriceBeforeStorage'
                  )}
                  description={t(
                    'result.analysis.storageTable.weightedAverageSalesPriceBeforeStorageDescription'
                  )}
                >
                  <IconInfo fill="black" />
                </Tooltip>
              </Flex>
              <Flex gap={9} align="center" className="column">
                <Typography.Text className="fs-14-bold">
                  {t(
                    'result.analysis.storageTable.weightedAverageSalesPriceAfterStorage'
                  )}
                  {showNumbers && (
                    <span className="text-number-color"> (3)</span>
                  )}
                </Typography.Text>
                <Tooltip
                  title={t(
                    'result.analysis.storageTable.weightedAverageSalesPriceAfterStorage'
                  )}
                  description={t(
                    'result.analysis.storageTable.weightedAverageSalesPriceAfterStorageDescription'
                  )}
                >
                  <IconInfo fill="black" />
                </Tooltip>
              </Flex>
            </Flex>
          </Col>
          <Col span={7}>
            <Flex vertical align="center">
              <Flex justify="center" align="center" className="value">
                <Typography.Text className="fs-14-regular text-gray-color">
                  {formatNumberWithCommas(
                    Number(
                      simulationData.result.averageEnergyMarketPrice.toFixed(2)
                    )
                  ) + ` ${currencySymbol}`}
                </Typography.Text>
              </Flex>
              <Flex justify="center" align="center" className="value">
                <Typography.Text className="fs-14-regular text-gray-color">
                  {formatNumberWithCommas(
                    Number(
                      simulationData.result.averageSalesPriceWos.toFixed(2)
                    )
                  ) + ` ${currencySymbol}`}
                </Typography.Text>
              </Flex>
              <Flex justify="center" align="center" className="value">
                <Typography.Text className="fs-14-regular text-gray-color">
                  {formatNumberWithCommas(
                    Number(simulationData.result.averageSalesPrice.toFixed(2))
                  ) + ` ${currencySymbol}`}
                </Typography.Text>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </Col>
      <Col span={14}>
        <Row>
          <Col span={10}>
            <Flex vertical>
              <Flex gap={8} align="center" className="column" justify="center">
                <Typography.Text className="fs-14-bold">
                  {t('result.analysis.storageTable.storagePremiumPerMwh')}
                  {showNumbers && (
                    <span className="text-number-color"> (4)</span>
                  )}
                </Typography.Text>
                <Tooltip
                  title={t('result.analysis.storageTable.storagePremiumPerMwh')}
                  description={t(
                    'result.analysis.storageTable.storagePremiumPerMwhDescription'
                  )}
                >
                  <IconInfo fill="black" />
                </Tooltip>
              </Flex>
              <Typography.Text className="fs-14-regular storage-value text-gray-color">
                {formatNumberWithCommas(
                  Number(simulationData.result.storagePremiumPerMwh.toFixed(2))
                ) + ` ${currencySymbol}`}
              </Typography.Text>
            </Flex>
          </Col>
          <Col span={14} style={{ height: '100%' }}>
            <Flex vertical>
              <Flex gap={8} align="center" className="column" justify="center">
                <Typography.Text className="fs-14-bold ">
                  {t(
                    'result.analysis.storageTable.weightedAverageMcpDuringDischarge'
                  )}
                  {showNumbers && (
                    <span className="text-number-color"> (5)</span>
                  )}
                </Typography.Text>
                <Tooltip
                  title={t(
                    'result.analysis.storageTable.weightedAverageMcpDuringDischarge'
                  )}
                  description={t(
                    'result.analysis.storageTable.weightedAverageMcpDuringDischargeDescription'
                  )}
                >
                  <IconInfo fill="black" />
                </Tooltip>
              </Flex>
              <Typography.Text className="fs-14-regular text-gray-color weighted-value">
                {formatNumberWithCommas(
                  Number(
                    simulationData.result.averageSalesPriceFromDischargeTotal.toFixed(
                      2
                    )
                  )
                ) + ` ${currencySymbol}`}
              </Typography.Text>
            </Flex>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Flex vertical align="center" justify="center">
          <Flex gap={9} align="center">
            <Typography.Text className="fs-14-bold battery-text">
              {t('result.analysis.storageTable.batteryUsageCost')}
              {showNumbers && <span className="text-number-color"> (6)</span>}
            </Typography.Text>
            <Tooltip
              title={t('result.analysis.storageTable.batteryUsageCost')}
              description={t(
                'result.analysis.storageTable.batteryUsageCostDescription',
                { symbol }
              )}
            >
              <IconInfo fill="black" />
            </Tooltip>
          </Flex>
          <Typography.Text className="fs-14-regular text-gray-color battery-value">
            {formatNumberWithCommas(
              Number(simulationData.result.batteryUsageCostForSale.toFixed(2))
            ) + ` ${currencySymbol}`}
          </Typography.Text>
        </Flex>
      </Col>
    </StyledStorage>
  );
};
export default StorageTable;
